import type { Directive, DirectiveBinding } from 'vue'
import { useDebounceFn } from '@vueuse/core'
import type { DebounceFilterOptions } from '@vueuse/core'

interface DebounceElement extends HTMLElement {
  // 存储防抖处理器
  _debounceHandler?: (event: Event) => void
  _debounceEventType?: string
}

// 指令配置类型
export interface DebounceConfig {
  wait?: number // 延迟时间，默认 300ms
  options?: DebounceFilterOptions // useDebounceFn 的 options 参数
}

/**
 * 防抖指令
 * @example
 * // 基础用法 - 默认 300ms 防抖 click 事件
 * <button @click="handleClick" v-debounce>点击</button>
 *
 * // 自定义延迟时间
 * <button @click="handleClick" v-debounce="500">点击</button>
 *
 * // 指定事件类型和延迟时间
 * <input @input="handleInput" v-debounce:input="500" />
 *
 * // 使用对象配置
 * <button @click="handleClick" v-debounce="{ wait: 500 }">点击</button>
 *
 * // 带选项配置
 * <button @click="handleClick" v-debounce="{ wait: 500, options: { maxWait: 1000, rejectOnCancel: false } }">点击</button>
 */
export const debounce: Directive = {
  mounted(el: DebounceElement, binding: DirectiveBinding) {
    const eventType = binding.arg || 'click' // 默认拦截 click 事件
    const { value } = binding

    // 解析配置
    let ms = 300 // 默认延迟时间
    let options: DebounceFilterOptions = {}

    if (typeof value === 'number') {
      // 数字：直接作为 wait
      ms = value
    } else if (typeof value === 'object' && value !== null) {
      // 对象：{ wait?, options? }
      const config = value as DebounceConfig
      if (typeof config.wait === 'number') {
        ms = config.wait
      }
      if (config.options) {
        options = config.options
      }
    }

    // 创建防抖的派发函数
    const debouncedDispatch = useDebounceFn(
      () => {
        // 临时移除拦截监听器
        el.removeEventListener(eventType, handler, true)

        // 创建新事件并派发（这会触发 Vue 的 @click 等监听器）
        const newEvent = new Event(eventType, {
          bubbles: true,
          cancelable: true,
        })
        el.dispatchEvent(newEvent)

        // 重新添加拦截监听器
        el.addEventListener(eventType, handler, true)
      },
      ms,
      options,
    )

    // 事件拦截器（每次点击都会立即执行，但派发是防抖的）
    const handler = (event: Event) => {
      // 阻止事件继续传播到 Vue 的监听器（在捕获阶段拦截）
      event.stopImmediatePropagation()
      event.preventDefault()

      // 调用防抖的派发函数
      debouncedDispatch()
    }

    // 保存处理器
    el._debounceHandler = handler
    el._debounceEventType = eventType

    // 在捕获阶段监听事件，优先级高于 Vue 的事件监听
    el.addEventListener(eventType, handler, true)
  },

  updated(el: DebounceElement, binding: DirectiveBinding) {
    const eventType = binding.arg || 'click'
    const { value, oldValue } = binding

    // 如果配置没有变化，不需要更新
    if (value === oldValue) {
      return
    }

    // 移除旧的监听器
    if (el._debounceHandler && el._debounceEventType) {
      el.removeEventListener(el._debounceEventType, el._debounceHandler, true)
    }

    // 解析新配置
    let ms = 300
    let options: DebounceFilterOptions = {}

    if (typeof value === 'number') {
      ms = value
    } else if (typeof value === 'object' && value !== null) {
      const config = value as DebounceConfig
      if (typeof config.wait === 'number') {
        ms = config.wait
      }
      if (config.options) {
        options = config.options
      }
    }

    // 创建防抖的派发函数
    const debouncedDispatch = useDebounceFn(
      () => {
        el.removeEventListener(eventType, handler, true)

        const newEvent = new Event(eventType, {
          bubbles: true,
          cancelable: true,
        })
        el.dispatchEvent(newEvent)

        el.addEventListener(eventType, handler, true)
      },
      ms,
      options,
    )

    // 事件拦截器
    const handler = (event: Event) => {
      event.stopImmediatePropagation()
      event.preventDefault()
      debouncedDispatch()
    }

    el._debounceHandler = handler
    el._debounceEventType = eventType

    el.addEventListener(eventType, handler, true)
  },

  unmounted(el: DebounceElement) {
    // 移除事件监听器
    if (el._debounceHandler && el._debounceEventType) {
      el.removeEventListener(el._debounceEventType, el._debounceHandler, true)
      delete el._debounceHandler
      delete el._debounceEventType
    }
  },
}
